import PropTypes from 'prop-types';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaw,
  faDog,
  faCat,
  faMapMarkerAlt,
  faVenus,
  faMars,
  faBirthdayCake,
  faExternalLinkAlt,
  faPhone,
  faEnvelope,
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import Button from './Button';

/**
 * Component to display real adoptable pets from Petfinder
 */
function AdoptablePets({ pets, loading, error, onRefresh, userLocation }) {
  const [selectedPet, setSelectedPet] = useState(null);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-primary-500 animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          Searching for Adoptable Pets
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300">
          Finding pets that match your preferences in your area...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="h-8 w-8 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          Unable to Load Pets
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300 mb-4">
          {error}
        </p>
        {onRefresh && (
          <Button variant="primary" onClick={onRefresh}>
            Try Again
          </Button>
        )}
      </div>
    );
  }

  if (!pets || pets.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
        <FontAwesomeIcon icon={faPaw} className="h-12 w-12 text-neutral-400 mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          No Pets Found
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300 mb-4">
          We couldn&apos;t find any adoptable pets matching your criteria in your area.
        </p>
        <div className="bg-amber-50 dark:bg-amber-900 rounded-lg p-4 mb-4">
          <p className="text-amber-700 dark:text-amber-200 text-sm">
            Try expanding your search radius or adjusting your preferences to see more results.
          </p>
        </div>
        {onRefresh && (
          <Button variant="outline" onClick={onRefresh}>
            Refresh Search
          </Button>
        )}
      </div>
    );
  }

  const getGenderIcon = (gender) => {
    return gender?.toLowerCase() === 'female' ? faVenus : faMars;
  };

  const getGenderColor = (gender) => {
    return gender?.toLowerCase() === 'female' 
      ? 'text-pink-500' 
      : 'text-blue-500';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (score >= 60) return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200';
  };

  const formatDistance = (distance) => {
    return distance ? `${Math.round(distance)} miles away` : '';
  };

  const getPrimaryPhoto = (photos) => {
    if (!photos || photos.length === 0) return null;
    return photos.find(photo => photo.medium) || photos[0];
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">
          Adoptable Pets Near You
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300">
          {pets.length} pets found matching your preferences
          {userLocation && ` in ${userLocation.zipCode || 'your area'}`}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pets.map((pet) => {
          const photo = getPrimaryPhoto(pet.photos);
          const contact = pet.contact || {};
          const attributes = pet.attributes || {};
          
          return (
            <button
              key={pet.id}
              type="button"
              className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer w-full text-left"
              onClick={() => setSelectedPet(pet)}
            >
              {/* Pet Photo */}
              <div className="relative h-48">
                {photo ? (
                  <img
                    src={photo.medium || photo.large || photo.small}
                    alt={pet.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/300x200/e5e7eb/6b7280?text=${encodeURIComponent(pet.name || 'Pet Photo')}`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                    <FontAwesomeIcon 
                      icon={pet.type?.toLowerCase() === 'cat' ? faCat : faDog} 
                      className="h-12 w-12 text-neutral-400"
                    />
                  </div>
                )}
                
                {/* Compatibility Score */}
                {pet.compatibilityScore && (
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(pet.compatibilityScore)}`}>
                      {pet.compatibilityScore}% Match
                    </span>
                  </div>
                )}

                {/* Status Badge */}
                {pet.status && (
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                      {pet.status}
                    </span>
                  </div>
                )}
              </div>

              {/* Pet Details */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {pet.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon 
                      icon={getGenderIcon(pet.gender)} 
                      className={`h-4 w-4 ${getGenderColor(pet.gender)}`}
                    />
                  </div>
                </div>

                {/* Basic Info */}
                <div className="space-y-1 mb-3">
                  <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-300">
                    <FontAwesomeIcon icon={faPaw} className="h-3 w-3 mr-2" />
                    {pet.breeds?.primary && (
                      <span>{pet.breeds.primary}</span>
                    )}
                    {pet.breeds?.mixed && (
                      <span className="ml-1">(Mixed)</span>
                    )}
                  </div>
                  
                  {pet.age && (
                    <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-300">
                      <FontAwesomeIcon icon={faBirthdayCake} className="h-3 w-3 mr-2" />
                      {pet.age} • {pet.size} • {pet.gender}
                    </div>
                  )}
                  
                  {pet.distance && (
                    <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-300">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="h-3 w-3 mr-2" />
                      {formatDistance(pet.distance)}
                    </div>
                  )}
                </div>

                {/* Key Attributes */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {attributes.spayed_neutered && (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                      Spayed/Neutered
                    </span>
                  )}
                  {attributes.house_trained && (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs">
                      House Trained
                    </span>
                  )}
                  {attributes.good_with_children && (
                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs">
                      Good with Kids
                    </span>
                  )}
                </div>

                {/* Description Preview */}
                {pet.description && (
                  <p className="text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2 mb-3">
                    {pet.description.length > 100 
                      ? `${pet.description.substring(0, 100)}...`
                      : pet.description
                    }
                  </p>
                )}

                {/* Contact Info */}
                {contact.email && (
                  <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Contact: {pet.organization_id || 'Shelter'}
                    </p>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Pet Details Modal */}
      {selectedPet && (
        <PetDetailsModal 
          pet={selectedPet} 
          onClose={() => setSelectedPet(null)} 
        />
      )}
    </div>
  );
}

/**
 * Modal component for detailed pet information
 */
function PetDetailsModal({ pet, onClose }) {
  const photo = pet.photos?.[0];
  const contact = pet.contact || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
            {pet.name}
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Photo */}
          {photo && (
            <img
              src={photo.large || photo.medium || photo.small}
              alt={pet.name}
              className="w-full h-64 object-cover rounded-lg mb-4"
            />
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Age</p>
              <p className="text-neutral-900 dark:text-white">{pet.age}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Size</p>
              <p className="text-neutral-900 dark:text-white">{pet.size}</p>
            </div>
          </div>

          {/* Description */}
          {pet.description && (
            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">About {pet.name}</p>
              <p className="text-neutral-600 dark:text-neutral-300">{pet.description}</p>
            </div>
          )}

          {/* Contact Information */}
          {(contact.email || contact.phone) && (
            <div className="bg-primary-50 dark:bg-primary-900 rounded-lg p-4">
              <h3 className="font-medium text-primary-900 dark:text-primary-100 mb-2">
                Contact Information
              </h3>
              
              {contact.email && (
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-2" />
                  <a 
                    href={`mailto:${contact.email}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {contact.email}
                  </a>
                </div>
              )}
              
              {contact.phone && (
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon icon={faPhone} className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-2" />
                  <a 
                    href={`tel:${contact.phone}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {contact.phone}
                  </a>
                </div>
              )}

              {pet.url && (
                <div className="mt-3">
                  <a
                    href={pet.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="h-4 w-4 mr-2" />
                    View on Petfinder
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

AdoptablePets.propTypes = {
  pets: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
  userLocation: PropTypes.object,
};

PetDetailsModal.propTypes = {
  pet: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdoptablePets;